import { ReactElement, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { AxiosResultDefaultError } from '../../../../../../api/request';
import { AuthenticationError } from '../../../../../../api/requests/tuitionProcess';
import { createFetchContext } from '../../../../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../../../../components/info/DisplayError';
import Loading from '../../../../../../components/info/Loading';
import { getDraftSurvey } from '../../../api';
import {
  SurveyDraft,
  SurveyDraftResponseType,
} from '../../../types/SurveyDraft';

export interface SurveyDraftEditLoaderProps {
  children: (props: {
    draft: SurveyDraft;
    refresh: () => Promise<void>;
  }) => ReactElement;
}

const { FetchProvider, FetchConsumer, useFetch } = createFetchContext<
  undefined,
  SurveyDraftResponseType,
  AuthenticationError | AxiosResultDefaultError
>();

export function useSurveyDraftEditLoader() {
  return useFetch();
}

export default function SurveyDraftEditLoader({
  children,
}: SurveyDraftEditLoaderProps) {
  const { draftId } = useParams<{ draftId: string }>();

  const request = useCallback(async () => {
    return getDraftSurvey(draftId);
  }, [draftId]);

  return (
    <FetchProvider request={request} defaultQuery={undefined} fetchImmediately>
      <FetchConsumer>
        {({ loading, data, error, refresh }) => {
          if (loading) {
            return <Loading insideCard />;
          }

          if (error) {
            return (
              <DisplayError
                insideCard
                textBody={error.code}
                retryAction={refresh}
              />
            );
          }

          if (!data) {
            return (
              <DisplayError
                insideCard
                textBody="No se cargaron los datos"
                retryAction={refresh}
              />
            );
          }

          return children({ draft: data, refresh });
        }}
      </FetchConsumer>
    </FetchProvider>
  );
}
